import { Link } from 'react-router-dom';

import { isNil } from 'lodash';

import { useMe } from 'entities/Auth/sdk';
import Abbreviations from 'entities/Home/components/Abbreviations';
import { fieldDefinitionsList } from 'entities/Home/constants';

import Accordion from 'components/Accordion';
import AccordionDetails from 'components/AccordionDetails';
import AccordionSummary from 'components/AccordionSummary';
import List from 'components/List';
import ListItem from 'components/ListItem';
import Text from 'components/Text';
import Typography from 'components/Text';

const HomePage = () => {
  const { data: me } = useMe();

  return (
    <div className="flex flex-col gap-3 p-12">
      {isNil(me) && (
        <div className="mb-6 flex justify-end">
          <Link
            to="/login"
            className="text-blue-600 underline hover:text-blue-800"
          >
            Log In
          </Link>
        </div>
      )}
      <Text variant="h4">About EdLight Research Portal</Text>
      <Text variant="subtitle1">
        At EdLight, we believe in the power of student work to drive equitable
        learning. Our Research Portal is designed to support education
        researchers with access to diverse, real-world student work datasets,
        grounded in authentic classroom practice.
      </Text>
      <Text variant="subtitle1">
        Through our Global Access Commitment, we ensure that research tools and
        insights remain accessible and affordable to advance learning science
        for all. By partnering with educators, districts, and researchers, we
        aim to build evidence-based solutions that truly impact teaching and
        learning.
      </Text>
      <Text variant="subtitle1">
        What We Offer:
        <List>
          <ListItem>
            • Authentic student work datasets to power research on learning and
            assessment.
          </ListItem>
          <ListItem>
            • Collaborative research opportunities to explore our 2M+ (and
            growing) math work annotations.
          </ListItem>
          <ListItem>
            • Ethical data stewardship ensuring student privacy and responsible
            AI devleopment.
          </ListItem>
        </List>
      </Text>

      <Text variant="subtitle1">
        <b>Join us in rethinking how we learn from student thinking</b> and
        learn more about{' '}
        <Link
          to="https://www.edlight.com/product"
          target="_blank"
          className="text-blue-600 underline hover:text-blue-800"
        >
          how EdLight is using this data.
        </Link>
      </Text>

      {!isNil(me) && <Abbreviations definitions={fieldDefinitionsList} />}

      <Text variant="h6">Explanation of the RPPL report.</Text>
      <Text variant="subtitle1">
        In the May 2025 report Using Student Work Data to Improve Teaching and
        Learning (Research Partnership for Professional Learning & EdLight), we
        examine how daily middle school math assignments, specifically
        Illustrative Math Cool Downs, can serve as a powerful, fine-grained
        indicator of student learning. Drawing on over 95,000 annotated
        assignments from grades 6-8 across three districts, each scored using
        rubrics and tagged for strategies and misconceptions by experts and AI,
        we found that student work patterns are highly predictive of end-of-year
        test scores and course grades, even after controlling for prior
        achievement. Certain error types, like incomplete responses or
        conceptual misunderstandings, are linked to lower performance, while
        using multiple problem-solving strategies predicts higher outcomes. We
        also uncovered consistent patterns in content areas where students excel
        or struggle, and small but meaningful subgroup differences that are
        largely explained by the types of misconceptions present. These findings
        underscore the value of systematic student work annotations: they not
        only provide actionable insight for teachers and curriculum developers,
        but also contextualize the EdLight repository’s data as a rich,
        predictive lens into student thinking and progress.
      </Text>

      <Text variant="h6">Global Access</Text>
      <Text variant="subtitle1">
        Commitment to make research tools developed under these projects
        accessible and affordable to other researchers and study requests for
        free or at cost, provided they are in service of the co-constructed
        research agenda.
      </Text>

      <Text variant="h6">
        Just below the sample research questions provide the following context:
      </Text>
      <Text variant="subtitle1">
        To ensure alignment with our mission and responsible data use, we
        evaluate all access requests based on the relevance and rigor of the
        proposed research. The sample research questions above are not
        exhaustive, but they illustrate the kinds of inquiries that align with
        our evaluation criteria. Researchers whose work reflects similar themes,
        such as equity-centered analysis, instructional insight, and the study
        of formative assessment practices, are more likely to receive approval
        for access.
      </Text>

      <Text variant="h4">FAQs</Text>
      <Accordion>
        <AccordionSummary>
          <Text variant="h6">What kind of data does EdLight provide?</Text>
        </AccordionSummary>
        <AccordionDetails>
          <Text variant="subtitle1">
            Our dataset consists of handwritten student math work, captured
            directly from classrooms. Each piece of work is tagged with
            annotations, including student strategies, misconceptions and
            instructional feedback.
          </Text>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary>
          <Text variant="h6">How We Protect Student Privacy</Text>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body1" paragraph>
            To ensure the ethical and responsible use of student work in the
            repository, we implemented a two-step process to remove all
            personally identifiable information (PII) before including any
            artifacts in the dataset:
          </Typography>

          <Typography variant="body1" component="div" paragraph>
            <ol className="list-decimal space-y-2 pl-4">
              <li>
                <strong>AI-Powered PII Detection and Removal</strong> <br />
                We used a PII detection tool powered by artificial intelligence
                to scan each student work sample for identifying information
                such as names, school names, dates, and locations. The tool
                automatically redacted or removed any detected PII before the
                sample progressed to the next stage.
              </li>
              <li>
                <strong>Human Review and Verification</strong> <br />
                After the automated pass, trained human annotators conducted a
                secondary review of each sample. Their role was to confirm the
                accuracy of the AI tool’s redactions and manually remove any PII
                that may have been missed. This human-in-the-loop process added
                a critical layer of quality control to protect student
                identities.
              </li>
            </ol>
          </Typography>

          <Typography variant="body1" paragraph>
            We take student privacy seriously and have designed this repository
            to prioritize data security, anonymity, and responsible access at
            every stage.
          </Typography>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary>
          <Text variant="h6">How can researchers use this data?</Text>
        </AccordionSummary>
        <AccordionDetails>
          <Text variant="subtitle1">
            Researchers can use our dataset to study student thinking, math
            learning progressions, AI-assisted feedback, and equitable
            assessment practices. The data cannot be used for commercial
            applications, product development, or any undisclosed purposes. Any
            attempt to use the data outside the approved research scope will
            result in immediate termination of access and potential legal
            action.
          </Text>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary>
          <Text variant="h6">Is the data anonymized?</Text>
        </AccordionSummary>
        <AccordionDetails>
          <Text variant="subtitle1">
            Yes! All student work is de-identified to protect privacy. Our
            research partnerships follow strict data ethics and security
            guidelines.
          </Text>
        </AccordionDetails>
      </Accordion>
      <Accordion>
        <AccordionSummary>
          <Text variant="h6">
            Can I request specific types of student work?
          </Text>
        </AccordionSummary>
        <AccordionDetails>
          <Text variant="subtitle1">
            We provide access based on broad research themes in mathematics. If
            you need a more specific dataset, reach out to discuss alignment
            with our Global Access Commitment.
          </Text>
        </AccordionDetails>
      </Accordion>

      <Text variant="subtitle2">
        Have more questions? Email us at <Link to="mailto:support@edlight.com">support@edlight.com</Link>.
      </Text>

      <Text variant="h5">How to Cite EdLight</Text>
      <Text variant="subtitle1">
        If you use data, examples, or insights from the EdLight Research Portal
        in your research, presentations, or publications, please cite the
        resource as follows:
      </Text>
      <Text variant="subtitle1" className="pl-5 font-bold">
        EdLight. (2024). EdLight Research Portal: Authentic Student Work
        Datasets for Education Research. Retrieved from
        https://www.edlight.com/research-portal
      </Text>
      <Text variant="h6">For APA Style:</Text>
      <Text variant="subtitle1" className="pl-5 font-bold">
        EdLight. (2025). EdLight Research Portal: Anonymized Student Work
        Repository for Educational Research. https://research.edlight.education
      </Text>

      <Text variant="h6">For APA Style:</Text>
      <Text variant="subtitle1" className="pl-5 font-bold">
        EdLight. (2025). EdLight Research Portal: Anonymized Student Work
        Repository for Educational Research. https://research.edlight.education
      </Text>

      <Text variant="h6">For MLA Style:</Text>
      <Text variant="subtitle1" className="pl-5 font-bold">
        EdLight. EdLight Research Portal: Anonymized Student Work Repository for
        Educational Research. 2025, https://research.edlight.education.
      </Text>

      <Text variant="h6">For Chicago Style:</Text>
      <Text variant="subtitle1" className="pl-5 font-bold">
        EdLight. EdLight Research Portal: Anonymized Student Work Repository for
        Educational Research. 2025. https://research.edlight.education.
      </Text>

      <Text variant="subtitle1">
        If you are referencing a specific dataset, annotation type, or research
        tool from the portal, we recommend including that in a footnote or
        methods section (e.g., “Dataset A from the EdLight Research Portal”).
      </Text>
      <br />
      <Text variant="subtitle1">
        We also welcome copies or summaries of your publications for learning
        and sharing purposes. Please email us at <Link to="mailto:support@edlight.com">support@edlight.com</Link>.
      </Text>
    </div>
  );
};

export default HomePage;
